import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@pickleballinc/react-ui';
import clsx from 'clsx';

import { Button } from '@/components/atoms';
import Link from 'next/link';
import { LineupEntryOrder } from '@/infrastructure/network/dto/SeasonDivisionPool';

interface ActionScoreCardsButtonsProps {
	onCancel?: () => void;
	onComplete?: () => void;
	onAddTieBreaker?: () => void;
	isDisabledComplete?: boolean;
	isDisabledTieBreaker?: boolean;
	completeButtonText?: string;
	cancelButtonText?: string;
	tieBreakerButtonText?: string;
	completeButtonTooltip?: string;
	teamLeagueId?: string;
	matchupId?: string;
	showTieBreakerButton?: boolean;
	isTie?: boolean;
	showCompleteButton?: boolean;
	allAwayPlayersSelected?: boolean;
	allHomePlayersSelected?: boolean;
	isAwayTeamCaptain?: boolean;
	isHomeTeamCaptain?: boolean;
	hasTeamTwoTieBreakerLineup?: boolean;
	hasTeamOneTieBreakerLineup?: boolean;
	canManageGameDayPermission?: boolean;
	onCompleteNotification?: () => void;
	lineupEntryOrder?: LineupEntryOrder;
	shouldShowTieBreakerButton?: boolean;
}

export const ActionScoreCardsButtons = ({
	onCancel,
	onComplete,
	onAddTieBreaker,
	isDisabledComplete = false,
	isDisabledTieBreaker = false,
	completeButtonText = 'Complete lineup',
	cancelButtonText = 'Cancel',
	tieBreakerButtonText = 'Select the tiebreaker lineup',
	completeButtonTooltip,
	teamLeagueId,
	matchupId,
	showTieBreakerButton = true,
	isTie = false,
	showCompleteButton = true,
	allAwayPlayersSelected,
	allHomePlayersSelected,
	isAwayTeamCaptain,
	isHomeTeamCaptain,
	hasTeamTwoTieBreakerLineup,
	hasTeamOneTieBreakerLineup,
	onCompleteNotification,
	lineupEntryOrder,
	shouldShowTieBreakerButton
}: ActionScoreCardsButtonsProps) => {
	const getTieBreakerStateMessage = () => {
		if ((isAwayTeamCaptain && !allAwayPlayersSelected) || (isHomeTeamCaptain && !allHomePlayersSelected)) {
			return 'First you have to select lineups for matches above.';
		}

		if (isHomeTeamCaptain && allHomePlayersSelected && !hasTeamTwoTieBreakerLineup) {
			if (lineupEntryOrder === LineupEntryOrder.AWAY_TEAM_FIRST_LINEUP_ENTRY)
				return 'Waiting for the away team to set their tiebreaker lineup first';
			if (lineupEntryOrder === LineupEntryOrder.EITHER_TEAM_FIRST_LINEUP_ENTRY && hasTeamOneTieBreakerLineup)
				return 'The away team must set their tiebreaker lineup.';
		}

		if (isAwayTeamCaptain && allAwayPlayersSelected && !hasTeamOneTieBreakerLineup) {
			if (lineupEntryOrder === LineupEntryOrder.HOME_TEAM_FIRST_LINEUP_ENTRY)
				return 'Waiting for the home team to set their tiebreaker lineup first';
			if (lineupEntryOrder === LineupEntryOrder.EITHER_TEAM_FIRST_LINEUP_ENTRY && hasTeamTwoTieBreakerLineup)
				return 'The home team must set their tiebreaker lineup.';
		}

		return '';
	};

	return (
		<div className={clsx('flex flex-col gap-4 sm:flex-row', !showTieBreakerButton ? 'sm:justify-end' : 'sm:justify-between')}>
			{showTieBreakerButton && (
				<>
					{shouldShowTieBreakerButton ? (
						<Tooltip placement="top">
							<Tooltip.Trigger asChild>
								<Link href={`/team-leagues/${teamLeagueId}/matchup/${matchupId}/tie-breaker`} prefetch={false}>
									<Button
										onClick={onAddTieBreaker}
										disabled={isDisabledTieBreaker}
										className={clsx('flex items-center justify-center gap-2 text-xs !sm:text-base font-semibold', {
											'cursor-not-allowed !bg-gray-100 !text-gray-400': isDisabledTieBreaker,
											'!border !border-gray-300 text-black  hover:shadow-sm': !isDisabledTieBreaker
										})}
									>
										<FontAwesomeIcon icon={faPlus} className="size-4" />
										{tieBreakerButtonText}
									</Button>
								</Link>
							</Tooltip.Trigger>
						</Tooltip>
					) : (
						<p className="text-sm text-gray-500 py-4">{getTieBreakerStateMessage()}</p>
					)}
				</>
			)}

			{showCompleteButton && (
				<div className="flex w-full flex-row justify-between gap-2 sm:w-auto sm:gap-4">
					{onCancel && (
						<Button
							onClick={onCancel}
							className="w-full border border-gray-300 !bg-white text-sm font-semibold text-gray-700 shadow-xs transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 hover:shadow-sm sm:w-auto"
						>
							{cancelButtonText}
						</Button>
					)}
					{!isTie && (
						<Tooltip placement="top">
							<Tooltip.Trigger asChild>
								<Button
									onClick={() => {
										onComplete?.();
										onCompleteNotification?.();
									}}
									disabled={isDisabledComplete}
									className={clsx('w-full border text-base font-semibold shadow-xs transition-all duration-200 sm:w-auto', {
										'cursor-not-allowed  border border-gray-200 !bg-gray-100 !text-gray-400': isDisabledComplete,
										'border-gray-200 !bg-orange-500 text-white hover:!bg-orange-700 hover:shadow-sm': !isDisabledComplete
									})}
								>
									{completeButtonText}
								</Button>
							</Tooltip.Trigger>
							{isDisabledComplete && completeButtonTooltip && (
								<Tooltip.Content>
									<p>{completeButtonTooltip}</p>
								</Tooltip.Content>
							)}
						</Tooltip>
					)}
				</div>
			)}
		</div>
	);
};
